import NavButton from '@atoms/NavButton/NavButton';
import BreakpointEnum from '@models/enums/BreakpointEnum';
import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import HorizontalDirectionEnum from '@models/enums/HorizontalDirectionEnum';
import Carousel from '@molecules/Carousel/Carousel';
import duplicateArrayItems from '@utils/helpers/duplicateArrayItems';
import useBreakpoint from '@utils/hooks/useBreakpoint';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import { ReactNode, useEffect, useState } from 'react';
import SwiperType from 'swiper';
import { Autoplay } from 'swiper/modules';
import { Swiper } from 'swiper/types';
import { AutoplayOptions } from 'swiper/types/modules/autoplay';

interface Props<T> {
  readonly id: string;
  readonly items: T[];
  readonly autoplay?: AutoplayOptions;
  readonly children: (itemsToDisplay: T[]) => ReactNode;
  readonly loop?: boolean;
}

const StripCarousel = <T,>({
  children,
  id,
  items,
  autoplay,
  loop = false,
}: Props<T>) => {
  const [shouldLoop, setShouldLoop] = useState(false);
  const [hasMoreItemsToShow, setHasMoreItemsToShow] = useState(false);
  const [swiperInstance, setSwiperInstance] = useState<Swiper>();
  const screenBreakpoint = useBreakpoint(BreakpointTypeEnum.Screen);
  const isMobileViewport = useMediaQuery(1024);
  const isMobileScreen = useMediaQuery(1024, BreakpointTypeEnum.Screen);
  const isZoomedDesktop = !isMobileScreen && isMobileViewport;

  const itemsToDisplay = shouldLoop
    ? duplicateArrayItems(items, items.length + 1)
    : items;

  const handleSwiperUpdate = (swiper: SwiperType) => {
    const swiperSlidesPerView = swiper?.params?.slidesPerView;
    const currentSlidesPerView =
      swiperSlidesPerView && typeof swiperSlidesPerView === 'number'
        ? swiperSlidesPerView
        : 0;

    setHasMoreItemsToShow(items.length > currentSlidesPerView);
  };

  useEffect(() => {
    const shouldLoopCondition =
      ((!!screenBreakpoint && screenBreakpoint !== BreakpointEnum.Small) ||
        loop) &&
      hasMoreItemsToShow;

    setShouldLoop(shouldLoopCondition);
  }, [screenBreakpoint, hasMoreItemsToShow, loop]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  useEffect(() => {
    // We need to re-initialize the navigation when viweport is changed betweeen mobile/desktop
    // as the nav buttons position is changed
    swiperInstance?.navigation.init();
  }, [isMobileViewport]);

  useEffect(() => {
    swiperInstance?.navigation.update();

    if (shouldLoop && autoplay) swiperInstance?.autoplay.start();
  }, [autoplay, shouldLoop, swiperInstance]);

  return (
    <div className="overflow-hidden">
      {isZoomedDesktop && (
        <div className="container mb-10 flex justify-end gap-x-[25px]">
          <NavButton
            direction={HorizontalDirectionEnum.Left}
            id={id}
            disabled={!shouldLoop}
          />

          <NavButton
            direction={HorizontalDirectionEnum.Right}
            id={id}
            disabled={!shouldLoop}
          />
        </div>
      )}

      <div className="overflow-swiper container lg:grid lg:grid-cols-12">
        <div className="col-span-full flex flex-row content-center items-center gap-13 3xl:px-32 px-0 xl:col-span-10 xl:col-start-2 xl:gap-[100px]">
          {!isMobileViewport && (
            <div>
              <NavButton
                direction={HorizontalDirectionEnum.Left}
                id={id}
                disabled={!shouldLoop}
              />
            </div>
          )}

          <Carousel
            spaceBetween={16}
            loop={shouldLoop}
            navId={id}
            className="overflow-large w-full"
            onResize={handleSwiperUpdate}
            onInit={handleSwiperUpdate}
            onSwiper={setSwiperInstance}
            additionalModules={autoplay ? [Autoplay] : undefined}
            autoplay={autoplay}
            breakpoints={{
              0: {
                // Swiper v10 needs a value greater than 1 to properly show the prev/next slides when looping
                slidesPerView: 1.001,
                centeredSlides: true,
              },
              1024: {
                slidesPerView: 1,
                centeredSlides: false,
              },
              1440: {
                slidesPerView: 2,
                centeredSlides: false,
              },
            }}
          >
            {children(itemsToDisplay)}
          </Carousel>

          {!isMobileViewport && (
            <div>
              <NavButton
                direction={HorizontalDirectionEnum.Right}
                id={id}
                disabled={!shouldLoop}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StripCarousel;
